/**
 * Codificación JPEG del fotograma FUERA del hilo principal.
 *
 * 🔴 Por qué existe: `canvas.toBlob` codifica en el hilo principal y solo en
 * sus ratos libres. Con la cámara abierta dentro de la app la pantalla baja a
 * 20–25 fps y no le quedan: medido en la Tab Active5 el 12-oct, entre 1,5 y
 * 5,3 s por foto (el botón «se apagaba varios segundos»). En la misma pantalla,
 * con el hilo ocupado 45 ms por fotograma, `toBlob` tardó 4,3 s y un worker con
 * `OffscreenCanvas` 0,2 s.
 *
 * Un solo worker y una sola codificación a la vez: cada fotograma son ~20 MB
 * de píxeles y en el WebView nada pesado va en paralelo
 * (`.claude/rules/webview-memoria-apk.md`).
 *
 * El worker se crea desde un Blob para no depender del empaquetador (ni de
 * `import.meta`, que Jest no entiende). Si algo falla, `canEncodeOffThread`
 * pasa a `false` y la captura vuelve al canvas de siempre.
 */

const WORKER_SOURCE = `
self.onmessage = async (event) => {
  const { id, bitmap, quality } = event.data;
  try {
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    canvas.getContext('2d').drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
    self.postMessage({ id, blob });
  } catch (error) {
    self.postMessage({ id, error: String(error) });
  }
};
`;

/**
 * Plazo por foto. Lo normal son 0,2–0,4 s; si el worker no contesta en este
 * tiempo se da por muerto. Sin plazo, un worker colgado dejaba el obturador
 * atenuado y «Hecho» esperando para siempre.
 */
const ENCODE_TIMEOUT_MS = 10_000;

interface Waiting {
  resolve: (blob: Blob) => void;
  reject: (error: Error) => void;
}

let worker: Worker | null = null;
/** Si el worker ha fallado una vez, no se vuelve a intentar en esta sesión. */
let broken = false;
let nextId = 0;
const waiting = new Map<number, Waiting>();
/** Fila de codificaciones: de una en una. */
let queue: Promise<unknown> = Promise.resolve();

export const canEncodeOffThread = (): boolean =>
  !broken &&
  typeof Worker !== 'undefined' &&
  typeof OffscreenCanvas !== 'undefined' &&
  typeof createImageBitmap === 'function';

const failAll = (error: Error): void => {
  waiting.forEach(({ reject }) => reject(error));
  waiting.clear();
};

/** El worker no sirve: falla lo que esperaba y no se vuelve a usar. */
const markBroken = (error: Error): void => {
  broken = true;
  failAll(error);
  worker?.terminate();
  worker = null;
};

const getWorker = (): Worker => {
  if (worker) return worker;
  if (broken) throw new Error('worker de la cámara no disponible');
  const url = URL.createObjectURL(
    new Blob([WORKER_SOURCE], { type: 'text/javascript' })
  );
  const created = new Worker(url);
  URL.revokeObjectURL(url);
  created.onmessage = (event: MessageEvent<{ id: number; blob?: Blob; error?: string }>) => {
    const { id, blob, error } = event.data;
    const pending = waiting.get(id);
    if (!pending) return;
    waiting.delete(id);
    if (blob) pending.resolve(blob);
    else pending.reject(new Error(error ?? 'el worker no devolvió la foto'));
  };
  created.onerror = (event) => {
    markBroken(new Error(`worker de la cámara caído: ${event.message}`));
  };
  worker = created;
  return created;
};

/**
 * Codifica el fotograma en JPEG en el worker. El `bitmap` se TRANSFIERE: tras
 * llamar a esto ya no se puede usar.
 */
export const encodeFrame = (bitmap: ImageBitmap, quality: number): Promise<Blob> => {
  const run = () =>
    new Promise<Blob>((resolve, reject) => {
      nextId += 1;
      const id = nextId;
      const timer = setTimeout(() => {
        if (waiting.has(id)) markBroken(new Error('el worker de la cámara no contestó a tiempo'));
      }, ENCODE_TIMEOUT_MS);
      waiting.set(id, {
        resolve: (blob) => {
          clearTimeout(timer);
          resolve(blob);
        },
        reject: (error) => {
          clearTimeout(timer);
          reject(error);
        },
      });
      try {
        getWorker().postMessage({ id, bitmap, quality }, [bitmap]);
      } catch (error) {
        // No llegó a transferirse: el fotograma (~20 MB) sigue siendo nuestro.
        bitmap.close();
        waiting.delete(id);
        clearTimeout(timer);
        if (!broken) markBroken(error instanceof Error ? error : new Error(String(error)));
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  const result = queue.then(run, run);
  queue = result.catch(() => undefined);
  return result;
};
